import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Database, User, Bell, Shield, Key, Globe, Mail, Smartphone, Save } from 'lucide-react';

const Card = ({ title, right, children }) => (
  <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const Input = ({ label, ...props }) => (
  <label className="block">
    <span className="text-sm text-gray-600 mb-1 block">{label}</span>
    <input {...props} className="w-full rounded-xl border border-gray-200 p-3 focus:ring-2 focus:ring-black focus:border-transparent" />
  </label>
);

const Integration = ({ label, status, description, connected }) => (
  <div className="border rounded-2xl p-4 flex items-center justify-between">
    <div className="flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
        connected ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
      }`}>
        <Globe size={20} />
      </div>
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </div>
    <span className={`text-xs px-3 py-1 rounded-full ${
      connected ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"
    }`}>
      {status}
    </span>
  </div>
);

function SettingsView() {
  const [activeSection, setActiveSection] = useState('profile');
  const [hasChanges, setHasChanges] = useState(false);
  
  const [profileData, setProfileData] = useState({
    brandName: 'PenGame Publishing, LLC',
    pressEmail: 'press@pengamepublishing.com',
    replyEmail: 'media@pengamepublishing.com',
    website: 'https://www.pengamepublishing.com',
    phone: '+1 (555) 123-4567',
    address: '123 Publishing Ave, Atlanta, GA 30309'
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailReports: true,
    campaignAlerts: true,
    deliveryNotifications: false,
    weeklyDigest: true,
    securityAlerts: true
  });

  const sections = [
    { id: 'profile', label: 'Brand Profile', icon: User },
    { id: 'integrations', label: 'Integrations', icon: Key },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'database', label: 'Contact Database', icon: Database },
  ];

  const integrations = [
    { label: "OpenAI", status: "Connected", description: "AI content generation", connected: true },
    { label: "Mailgun", status: "Connected", description: "Email delivery service", connected: true },
    { label: "SendGrid", status: "Not connected", description: "Backup email service", connected: false },
    { label: "Google Analytics", status: "Connected", description: "Website tracking", connected: true },
    { label: "Bitly", status: "Connected", description: "Link shortening", connected: true },
    { label: "Zapier", status: "Not connected", description: "Workflow automation", connected: false },
  ];

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleNotificationChange = (setting) => {
    setNotificationSettings(prev => ({ ...prev, [setting]: !prev?.[setting] }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Save settings logic here
    setHasChanges(false);
    alert('Settings saved successfully!');
  };

  return (
    <div className="grid lg:grid-cols-4 gap-6">
      {/* Sidebar Navigation */}
      <div className="lg:col-span-1">
        <Card title="Settings">
          <nav className="space-y-1">
            {sections?.map((section) => {
              const IconComponent = section?.icon;
              return (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition ${
                    activeSection === section?.id
                      ? 'bg-black text-white' :'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <IconComponent size={16} />
                  <span className="text-sm font-medium">{section?.label}</span>
                </button>
              );
            })}
          </nav>
        </Card>
      </div>
      {/* Main Content */}
      <div className="lg:col-span-3 space-y-6">
        {/* Brand Profile Section */}
        {activeSection === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card title="Brand Profile" right={
              hasChanges && (
                <button 
                  onClick={handleSave}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition"
                >
                  <Save size={14} />
                  Save Changes
                </button>
              )
            }>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input 
                  label="Brand Name" 
                  value={profileData?.brandName}
                  onChange={(e) => handleProfileChange('brandName', e?.target?.value)}
                />
                <Input 
                  label="Press Email" 
                  type="email"
                  value={profileData?.pressEmail}
                  onChange={(e) => handleProfileChange('pressEmail', e?.target?.value)}
                />
                <Input 
                  label="Reply-To Email" 
                  type="email"
                  value={profileData?.replyEmail}
                  onChange={(e) => handleProfileChange('replyEmail', e?.target?.value)}
                />
                <Input 
                  label="Website" 
                  type="url"
                  value={profileData?.website}
                  onChange={(e) => handleProfileChange('website', e?.target?.value)}
                />
                <Input 
                  label="Phone Number" 
                  type="tel"
                  value={profileData?.phone}
                  onChange={(e) => handleProfileChange('phone', e?.target?.value)}
                />
                <div className="sm:col-span-2">
                  <Input 
                    label="Business Address" 
                    value={profileData?.address}
                    onChange={(e) => handleProfileChange('address', e?.target?.value)}
                  />
                </div>
              </div>
            </Card>

            <Card title="Branding Options">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Custom Logo</p>
                    <p className="text-sm text-gray-500">Upload your brand logo for press releases</p>
                  </div>
                  <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                    Upload Logo
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Signature</p>
                    <p className="text-sm text-gray-500">Custom signature for outreach emails</p>
                  </div>
                  <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                    Edit Signature
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Integrations Section */}
        {activeSection === 'integrations' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card title="API Keys & Integrations" right={
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Secure</span>
            }>
              <div className="grid gap-3">
                {integrations?.map((integration, index) => (
                  <Integration key={index} {...integration} />
                ))}
              </div>
            </Card>

            <Card title="API Access">
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">API Documentation</h4>
                  <p className="text-sm text-blue-600 mb-3">
                    Integrate PenGame PR Distribution with your existing workflows using our REST API.
                  </p>
                  <button className="text-sm text-blue-700 underline hover:text-blue-800">
                    View API Docs
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">API Key</p>
                    <p className="text-sm text-gray-500">Generate API key for programmatic access</p>
                  </div>
                  <button className="px-4 py-2 bg-black text-white rounded-xl hover:bg-gray-800 transition">
                    Generate Key
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Notifications Section */}
        {activeSection === 'notifications' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card title="Notification Preferences">
              <div className="space-y-4">
                {Object.entries(notificationSettings)?.map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium capitalize">{key?.replace(/([A-Z])/g, ' $1')?.trim()}</p>
                      <p className="text-sm text-gray-500">
                        {key === 'emailReports' && 'Receive campaign performance reports via email'}
                        {key === 'campaignAlerts' && 'Get notified when campaigns start or complete'}
                        {key === 'deliveryNotifications' && 'Real-time notifications for email deliveries'}
                        {key === 'weeklyDigest' && 'Weekly summary of all PR activities'}
                        {key === 'securityAlerts' && 'Important security and account notifications'}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={() => handleNotificationChange(key)}
                        className="sr-only peer"
                      />
                      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Delivery Preferences">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="text-gray-400" size={20} />
                  <div className="flex-1">
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-gray-500">Receive updates via email</p>
                  </div>
                  <span className="text-sm text-green-600">Active</span>
                </div>
                
                <div className="flex items-center gap-3">
                  <Smartphone className="text-gray-400" size={20} />
                  <div className="flex-1">
                    <p className="font-medium">SMS Notifications</p>
                    <p className="text-sm text-gray-500">Critical alerts via text message</p>
                  </div>
                  <button className="text-sm text-blue-600 hover:text-blue-800">Setup</button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Security Section */}
        {activeSection === 'security' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card title="Account Security">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Two-Factor Authentication</p>
                    <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                  </div>
                  <button className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition">
                    Enable 2FA
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Change Password</p>
                    <p className="text-sm text-gray-500">Update your account password</p>
                  </div>
                  <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                    Change Password
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Login Sessions</p>
                    <p className="text-sm text-gray-500">Manage active login sessions</p>
                  </div>
                  <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                    View Sessions
                  </button>
                </div>
              </div>
            </Card>

            <Card title="Data & Privacy">
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2">GDPR Compliance</h4>
                  <p className="text-sm text-amber-600">
                    We're fully GDPR compliant. Manage your data preferences and export options here.
                  </p>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Export Data</p>
                    <p className="text-sm text-gray-500">Download all your account data</p>
                  </div>
                  <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                    Export
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Delete Account</p>
                    <p className="text-sm text-gray-500">Permanently delete your account and data</p>
                  </div>
                  <button className="px-4 py-2 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition">
                    Delete Account
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Database Section */}
        {activeSection === 'database' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card title="Contact Database">
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <div className="text-2xl font-bold text-blue-600">12,842</div>
                    <div className="text-sm text-blue-600">Total Contacts</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <div className="text-2xl font-bold text-green-600">8,934</div>
                    <div className="text-sm text-green-600">Active Contacts</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-xl">
                    <div className="text-2xl font-bold text-purple-600">24</div>
                    <div className="text-sm text-purple-600">Contact Lists</div>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-xl">
                    <div className="text-2xl font-bold text-amber-600">156</div>
                    <div className="text-sm text-amber-600">New This Month</div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button className="flex-1 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                    Import CSV
                  </button>
                  <button className="flex-1 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                    Export Database
                  </button>
                  <button className="flex-1 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition">
                    Manage Lists
                  </button>
                </div>
              </div>
            </Card>

            <Card title="Database Health">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Database size={16} className="text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800">Database Sync</p>
                      <p className="text-sm text-green-600">Last updated 2 hours ago</p>
                    </div>
                  </div>
                  <span className="text-green-600 font-medium">Healthy</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Shield size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-blue-800">Data Encryption</p>
                      <p className="text-sm text-blue-600">AES-256 encryption active</p>
                    </div>
                  </div>
                  <span className="text-blue-600 font-medium">Secured</span>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default SettingsView;